/**
 * 菜单接口。
 *
 * `MenuNode` 从 OpenAPI 生成而不是手写：手写的那份漂过一次（后端加了 `show_status`
 * 而前端类型没有，侧栏过滤编译不过、隐藏项照样画出来）。字段说明是后端 schema 上的
 * `description`，经生成进 `openapi.d.ts`，是唯一真源。
 */
import type { components } from '@/types/openapi'

import { del, get, patch, post, put } from './client'

/** `GET /menus/me` 的节点。侧栏过滤在 `stores/menu.ts` 的 `uiTree`，注册路由时不跳过。 */
export type MenuNode = components['schemas']['MenuNode']

export interface MenuTree {
  items: MenuNode[]
  total: number
}

export function fetchMyMenus(): Promise<MenuTree> {
  return get<MenuTree>('/menus/me')
}

// ── 管理端（全部要求 superadmin）──────────────────────────────
//
// 这几张表**没有租户列**：`roles` 是全局三行，`admin_role_menu` 按 role_code 存。
// 所以「给 admin 角色配菜单」改的是每个租户的 admin 看到的东西 ——
// 后端因此把这些端点锁在 superadmin。前端不重复判权限，403 由拦截器统一处理。

// 用 `type` 而不是 `interface`：AppTable 的 rows 是 `Record<string, unknown>[]`，
// 而 TS 只给类型别名隐式索引签名，interface 传进去会报「缺少索引签名」。
export type MenuAdminItem = {
  menu_code: string
  parent_code: string | null
  menu_name: string
  menu_type: string
  route_name: string | null
  route_path: string | null
  component: string | null
  icon_code: string | null
  sort_no: number
  /** `0` 隐藏 / `1` 显示。隐藏只影响侧栏，不影响能不能直接输 URL 进去。 */
  show_status: string
  perm_code: string | null
  /** 仅平台超管可见：非超管的 `/menus/me` 不下发这一行；目录为真时其下页面一并不下发。 */
  superadmin_only: boolean
}

/** 新建/修改菜单的入参。与 `MenuAdminItem` 差一个 `menu_code`（由序列生成）。 */
export type MenuUpsert = Omit<MenuAdminItem, 'menu_code'>

export type MenuAdminNode = MenuAdminItem & { children: MenuAdminNode[] }

export interface MenuAdminTree {
  items: MenuAdminNode[]
  total: number
}

export type MenuStructureItem = Pick<MenuAdminItem, 'menu_code' | 'parent_code' | 'sort_no'>

export interface RoleItem {
  code: string
  label_zh: string
  rank: number
  base_role: 'admin' | 'trainer' | 'viewer'
  is_builtin: boolean
}

export function listAllMenus(): Promise<MenuAdminTree> {
  return get<MenuAdminTree | MenuAdminItem[]>('/menus').then((response) => {
    if (!Array.isArray(response)) return response
    const childrenByParent = new Map<string, MenuAdminNode[]>()
    const roots: MenuAdminNode[] = []
    for (const item of response) {
      const node: MenuAdminNode = { ...item, children: [] }
      if (!item.parent_code) roots.push(node)
      else {
        const children = childrenByParent.get(item.parent_code) ?? []
        children.push(node)
        childrenByParent.set(item.parent_code, children)
      }
    }
    for (const node of roots) node.children = childrenByParent.get(node.menu_code) ?? []
    return { items: roots, total: response.length }
  })
}

export function saveMenuStructure(items: MenuStructureItem[]): Promise<MenuAdminTree> {
  return put<MenuAdminTree>('/menus/structure', { items })
}

export function createMenu(body: MenuUpsert): Promise<MenuAdminItem> {
  return post<MenuAdminItem>('/menus', body)
}

export function updateMenu(code: string, body: MenuUpsert): Promise<MenuAdminItem> {
  return patch<MenuAdminItem>(`/menus/${code}`, body)
}

export function deleteMenu(code: string): Promise<void> {
  return del(`/menus/${code}`)
}

export function flattenAdminTree(nodes: readonly MenuAdminNode[]): MenuAdminItem[] {
  return nodes.flatMap((node) => {
    const { children, ...item } = node
    return [item, ...flattenAdminTree(children)]
  })
}

export function listRoles(): Promise<RoleItem[]> {
  return get<RoleItem[]>('/menus/roles')
}

export function listAssignableRoles(): Promise<RoleItem[]> {
  return get<RoleItem[]>('/menus/roles/assignable')
}

export function createRole(body: Pick<RoleItem, 'label_zh' | 'base_role'>): Promise<RoleItem> {
  return post<RoleItem>('/menus/roles', body)
}

export function updateRole(
  code: string,
  body: Pick<RoleItem, 'label_zh' | 'base_role'>,
): Promise<RoleItem> {
  return patch<RoleItem>(`/menus/roles/${code}`, body)
}

export function deleteRole(code: string): Promise<void> {
  return del(`/menus/roles/${code}`)
}

export function getRoleMenus(roleCode: string): Promise<string[]> {
  return get<string[]>(`/menus/roles/${roleCode}/menus`)
}

/** 整体覆盖，不是增量 —— 勾选框的语义就是「这一屏即最终状态」。 */
export function setRoleMenus(roleCode: string, menuCodes: string[]): Promise<string[]> {
  return put<string[]>(`/menus/roles/${roleCode}/menus`, { menu_codes: menuCodes })
}

/** 深度优先摊平菜单树 —— 注册路由要平表，渲染侧栏要树。 */
export function flatten(nodes: readonly MenuNode[]): MenuNode[] {
  const out: MenuNode[] = []
  const walk = (list: readonly MenuNode[]): void => {
    for (const n of list) {
      out.push(n)
      // `children` 在生成的类型里是可选的（Pydantic 的 default_factory 不进 required），类型上要兜。
      if (n.children?.length) walk(n.children)
    }
  }
  walk(nodes)
  return out
}
